import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid-pro";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import {
  fetchDocument,
  fetchWaitingRooms,
  massAccept,
  massDecline,
} from "./api";
import { FilterModel, IGroupedWaitingRoom, PagedContent } from "./types";

export const useDataGridQueryWaitingRooms = () => {
  const companyId = useCompanyId();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [filters, setFilters] = useState<FilterModel>({});

  const queryParams = useMemo(
    () => ({
      ...filters,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
      sort: sortModel?.map(({ field, sort }) => `${field}:${sort}`),
    }),
    [filters, paginationModel.page, paginationModel.pageSize, sortModel]
  );

  const { data, ...restQuery } = useQuery({
    queryKey: ["waiting-rooms", companyId, queryParams],
    queryFn: fetchWaitingRooms(companyId, queryParams),
  });

  const rowCountRef = useRef(data?.totalElements || 0);
  const rowCount = useMemo(() => {
    if (data?.totalElements !== undefined) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  return {
    filters,
    setFilters,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    rows: data?.content || [],
    rowCount,
    ...restQuery,
  };
};

export const useMassAccept = () =>
  useMutation({
    mutationFn: massAccept,
    onError: async (_error, _variables, _onMutateResult, context) => {
      await context.client.invalidateQueries({ queryKey: ["waiting-rooms"] });
    },
    onSuccess(_data, _variables, _onMutateResult, context) {
      context.client.invalidateQueries({ queryKey: ["waiting-rooms"] });
    },
  });

export const useMassDecline = () =>
  useMutation({
    mutationFn: massDecline,

    onError: async (_error, _variables, _onMutateResult, context) => {
      await context.client.invalidateQueries({ queryKey: ["waiting-rooms"] });
    },
    onSuccess(_data, _variables, _onMutateResult, context) {
      context.client.invalidateQueries({ queryKey: ["waiting-rooms"] });
    },
  });

export const useQuerySalaryDeductionDocument = (documentId: string) => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["waiting-rooms-document", documentId],
    queryFn: fetchDocument(documentId),
    refetchInterval: 10000,
    retry: 1,
    retryDelay: 3000,
  });

  useEffect(() => {
    if (!query.isFetching && query.isSuccess && query.data?.documentActive) {
      const {
        documentId,
        documentActive,
        documentUrl,
        salaryDeductionAccepted,
      } = query.data;
      queryClient.setQueriesData(
        { queryKey: ["waiting-rooms", companyId] },
        (oldData: PagedContent<IGroupedWaitingRoom>) => {
          if (oldData) {
            return {
              ...oldData,
              content: oldData.content.map((row) =>
                row.waitingRoomsGroup?.groupId === documentId
                  ? {
                      ...row,
                      waitingRoomsGroup: {
                        ...row.waitingRoomsGroup,
                        documentActive,
                        documentUrl,
                        salaryDeductionAccepted,
                      },
                    }
                  : row
              ),
            };
          }
          return oldData;
        }
      );
    }
  }, [
    companyId,
    query.data,
    query.data?.documentActive,
    query.isFetching,
    query.isSuccess,
    queryClient,
  ]);

  return query;
};
